var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { UserPermissions, UserType } from "healthex-common/user.dtos";
import { filterNull } from "healthex-common/utils";
import { computed, observable, reaction } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { NamedRoutes } from "./NamedRoutes";
import { SimpleDialogModel } from "./SimpleDialogModel";
export class GoLiveChecklistViewModel {
    constructor(container) {
        this.goLiveConfirmDialogModel = new SimpleDialogModel();
        this.goLiveSuccessDialogModel = new SimpleDialogModel();
        this.inviteUsersDialogOpen = false;
        this.checklist = NOT_LOADED;
        this.isGoLiveLoading = false;
        this.authManager = container.authManager;
        this.provisioningService = container.provisioningService;
        this.organizationsManager = container.organizationsManager;
        this.adminUIManager = container.adminUIManager;
        this.appUIManager = container.appUIManager;
        this.routingUIManager = container.routingUIManager;
        this.patientInvitationUIManager = container.patientInvitationUIManager;
        reaction(() => this.orgId, async () => {
            await this.loadChecklist();
        }, { fireImmediately: true });
        reaction(() => this.adminUIManager.goLiveChecklistDialogModel.open, async () => {
            await this.loadChecklist();
        }, { fireImmediately: false });
        reaction(() => this.routingUIManager.currentRoute, async () => {
            if (this.isOpenChecklistDialog) {
                this.closeChecklistDialog();
            }
            if (this.goLiveConfirmDialogModel.open) {
                this.goLiveConfirmDialogModel.onChoice(false);
            }
            if (this.goLiveSuccessDialogModel.open) {
                this.goLiveSuccessDialogModel.onChoice(null);
            }
        }, { fireImmediately: true });
    }
    inviteUsers() {
        this.inviteUsersDialogOpen = true;
    }
    async inviteUsersDone() {
        this.inviteUsersDialogOpen = false;
    }
    get orgId() {
        var _a;
        return (_a = this.authManager.orgId) !== null && _a !== void 0 ? _a : null;
    }
    async loadChecklist() {
        if (this.orgId === null)
            return;
        this.checklist = await this.provisioningService.getOrganizationGoLiveChecklist(this.orgId);
        if (this.checklist) {
            await this.authManager.updateOrganizationIsLive(this.checklist.isLive);
        }
    }
    get progress() {
        if (this.checklist === NOT_LOADED || this.checklist === null)
            return null;
        const items = filterNull([
            this.checklist.logoAdded,
            this.checklist.patientAuthenticationAdded,
            this.checklist.dataConnectionAdded,
            this.checklist.dataDeliveryConfigured,
        ]);
        const totalItems = items.length;
        const itemsCompleted = items.reduce((sum, item) => sum + (item ? 1 : 0), 0);
        const percentage = `${Math.ceil((itemsCompleted / totalItems) * 100)}%`;
        const checklistCompleted = itemsCompleted === totalItems;
        return { totalItems, itemsCompleted, percentage, checklistCompleted };
    }
    get progressPercentage() {
        var _a, _b;
        return (_b = (_a = this.progress) === null || _a === void 0 ? void 0 : _a.percentage) !== null && _b !== void 0 ? _b : "0%";
    }
    get checklistCompleted() {
        var _a;
        return ((_a = this.progress) === null || _a === void 0 ? void 0 : _a.checklistCompleted) === true;
    }
    get canGoLive() {
        return this.checklistCompleted && (this.isITAdmin || this.isResearchAdmin);
    }
    get logoAdded() {
        if (this.checklist === NOT_LOADED || this.checklist === null)
            return null;
        return this.checklist.logoAdded;
    }
    get patientAuthenticationAdded() {
        if (this.checklist === NOT_LOADED || this.checklist === null)
            return null;
        return this.checklist.patientAuthenticationAdded;
    }
    get dataConnectionAdded() {
        if (this.checklist === NOT_LOADED || this.checklist === null)
            return null;
        return this.checklist.dataConnectionAdded;
    }
    get dataDeliveryConfigured() {
        if (this.checklist === NOT_LOADED || this.checklist === null)
            return null;
        return this.checklist.dataDeliveryConfigured;
    }
    get showLogoAdded() {
        return this.logoAdded !== null;
    }
    get showPatientAuthenticationAdded() {
        return this.patientAuthenticationAdded !== null;
    }
    get showDataConnectionAdded() {
        return this.dataConnectionAdded !== null;
    }
    get showDataDeliveryConfigured() {
        return this.dataDeliveryConfigured !== null;
    }
    get isResearchAdmin() {
        return this.authManager.permissions.includes(UserPermissions.RESEARCH_ADMIN);
    }
    get isITAdmin() {
        return this.authManager.permissions.includes(UserPermissions.ORG_ADMIN);
    }
    goToHomeFromDialog() {
        this.appUIManager.navigateToPage("HEALTHCARE_ORG_ANALYTICS");
        this.closeChecklistDialog();
    }
    get isOpenChecklistDialog() {
        return !!this.adminUIManager.goLiveChecklistDialogModel.open;
    }
    closeChecklistDialog() {
        this.adminUIManager.closeGoLiveChecklistDialogModel();
    }
    goToAddLogo() {
        if (!this.isITAdmin)
            return;
        this.adminUIManager.setCurrentAdminPage("MY_ORG", "DETAILS");
    }
    goToReviewPatientAuthentication() {
        if (!this.isResearchAdmin)
            return;
        this.adminUIManager.setCurrentAdminPage("PATIENT_SETTINGS", "PATIENT_AUTHENTICATION");
    }
    goToAddDataConnection() {
        if (!(this.isITAdmin || this.isResearchAdmin))
            return;
        this.adminUIManager.setCurrentAdminPage("DATA_SETTINGS", "CONNECTION");
    }
    goToAddDataDelivery() {
        if (!this.isITAdmin)
            return;
        this.adminUIManager.setCurrentAdminPage("DATA_SETTINGS", "DELIVERY");
    }
    async goLive() {
        var _a;
        if (this.isGoLiveLoading)
            return;
        if (!(this.isITAdmin || this.isResearchAdmin))
            return;
        if (((_a = this.progress) === null || _a === void 0 ? void 0 : _a.checklistCompleted) !== true)
            return;
        this.isGoLiveLoading = true;
        this.adminUIManager.closeGoLiveChecklistDialogModel();
        const confirmed = await this.goLiveConfirmDialogModel.showAndWait();
        if (confirmed) {
            await this.goLiveApi();
            this.goLiveSuccessDialogModel.show();
        }
        this.isGoLiveLoading = false;
    }
    async goLiveApi() {
        if (this.orgId === null || this.authManager.userType === null)
            return;
        if (this.authManager.userType !== UserType.HEALTHCARE_ORGANIZATION && this.authManager.userType !== UserType.EXTERNAL_RESEARCHER) {
            return;
        }
        await this.organizationsManager.updateOrganizationDetails(this.orgId, {
            type: this.authManager.userType,
            isLive: true,
        });
    }
    async goLiveSuccessOnCloseChoice(choice) {
        if (choice === null || choice) {
            this.goLiveSuccessDialogModel.open = false;
            await this.authManager.refreshTokenInfo();
        }
        if (choice === false) {
            await this.copyInvitationLink();
        }
        else if (choice) {
            this.routingUIManager.navigateTo(NamedRoutes.INDEX);
        }
    }
    get invitationLink() {
        return this.patientInvitationUIManager.invitationLink;
    }
    async copyInvitationLink() {
        await this.patientInvitationUIManager.copyInvitationLink();
    }
    get hasMultipleAdminRoles() {
        return this.authManager.userType === UserType.HEALTHCARE_ORGANIZATION;
    }
}
__decorate([
    observable
], GoLiveChecklistViewModel.prototype, "inviteUsersDialogOpen", void 0);
__decorate([
    observable
], GoLiveChecklistViewModel.prototype, "checklist", void 0);
__decorate([
    computed
], GoLiveChecklistViewModel.prototype, "orgId", null);
__decorate([
    computed
], GoLiveChecklistViewModel.prototype, "progress", null);
__decorate([
    computed
], GoLiveChecklistViewModel.prototype, "progressPercentage", null);
__decorate([
    computed
], GoLiveChecklistViewModel.prototype, "checklistCompleted", null);
__decorate([
    computed
], GoLiveChecklistViewModel.prototype, "canGoLive", null);
__decorate([
    computed
], GoLiveChecklistViewModel.prototype, "logoAdded", null);
__decorate([
    computed
], GoLiveChecklistViewModel.prototype, "patientAuthenticationAdded", null);
__decorate([
    computed
], GoLiveChecklistViewModel.prototype, "dataConnectionAdded", null);
__decorate([
    computed
], GoLiveChecklistViewModel.prototype, "dataDeliveryConfigured", null);
__decorate([
    computed
], GoLiveChecklistViewModel.prototype, "showLogoAdded", null);
__decorate([
    computed
], GoLiveChecklistViewModel.prototype, "showPatientAuthenticationAdded", null);
__decorate([
    computed
], GoLiveChecklistViewModel.prototype, "showDataConnectionAdded", null);
__decorate([
    computed
], GoLiveChecklistViewModel.prototype, "showDataDeliveryConfigured", null);
__decorate([
    computed
], GoLiveChecklistViewModel.prototype, "isResearchAdmin", null);
__decorate([
    computed
], GoLiveChecklistViewModel.prototype, "isITAdmin", null);
__decorate([
    computed
], GoLiveChecklistViewModel.prototype, "isOpenChecklistDialog", null);
__decorate([
    computed
], GoLiveChecklistViewModel.prototype, "invitationLink", null);
__decorate([
    computed
], GoLiveChecklistViewModel.prototype, "hasMultipleAdminRoles", null);
